import { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { CheckCircle, XCircle, Loader2, Mail } from "lucide-react";
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api/client';
import { AuthLayout } from '@/layouts';
import { VStack, HStack } from '@/components/core';

const EmailVerification = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { t } = useTranslation('emailVerification');
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [message, setMessage] = useState('');

  const token = searchParams.get('token');

  useEffect(() => {
    if (!token) {
      setStatus('error');
      setMessage(t('messages.noToken'));
      return;
    }

    const verifyEmail = async () => {
      try {
        await apiClient.post('/auth/verify-email', { token });
        setStatus('success');
        setMessage(t('messages.success'));

        // Invalidate user state so verification banner disappears without hard refresh
        void queryClient.invalidateQueries({ queryKey: ['current-user'] });

        // Redirect to login after 3 seconds
        setTimeout(() => {
          navigate('/auth');
        }, 3000);

      } catch (error: unknown) {
        setStatus('error');
        setMessage(t('messages.error'));
      }
    };

    void verifyEmail();
  }, [token, navigate]);

  const getIcon = () => {
    switch (status) {
      case 'loading':
        return <Loader2 className="h-16 w-16 text-info animate-spin" />;
      case 'success':
        return <CheckCircle className="h-16 w-16 text-success" />;
      case 'error':
        return <XCircle className="h-16 w-16 text-error" />;
    }
  };

  const getTitle = () => {
    switch (status) {
      case 'loading':
        return t('title.loading');
      case 'success':
        return t('title.success');
      case 'error':
        return t('title.error');
    }
  };

  return (
    <AuthLayout
      showBranding={false}
      maxWidth="md"
      className="design-page-gradient"
    >
      <Card className="glass-effect w-full">
        <CardHeader className="text-center">
          <VStack gap="md" align="center">
            {getIcon()}
            <CardTitle className="text-white text-2xl">
              {getTitle()}
            </CardTitle>
          </VStack>
        </CardHeader>
        <CardContent>
          <VStack gap="md" align="center" className="text-center">
            <p className="text-muted">
              {message}
            </p>

            {status === 'success' && (
              <VStack gap="md" fullWidth>
                <HStack gap="sm" align="center" justify="center" className="text-sm text-muted">
                  <Mail className="h-4 w-4" />
                  <span>{t('messages.redirecting')}</span>
                </HStack>
                <div className="w-full bg-white/10 rounded-full h-2">
                  <div className="bg-gradient-to-r from-brand-secondary to-brand-primary h-2 rounded-full animate-pulse"></div>
                </div>
              </VStack>
            )}

            {status === 'error' && (
              <VStack gap="md" fullWidth>
                <Button
                  onClick={() => { navigate('/auth'); }}
                  className="w-full bg-gradient-to-r from-brand-secondary to-brand-primary hover:from-brand-secondary-dark hover:to-brand-primary-dark"
                >
                  {t('buttons.toLogin')}
                </Button>
                <p className="text-muted text-sm">
                  {t('messages.supportContact')}
                </p>
              </VStack>
            )}
          </VStack>
        </CardContent>
      </Card>
    </AuthLayout>
  );
};

export default EmailVerification;
